import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";

function routeByRole(user) {
  if (user.role === "admin") return "/admin/dashboard";
  if (user.role === "attorney" || user.role === "law_firm") {
    if (user.status !== "approved") return "/pending";
    if (user.subscription_status !== "active" && user.subscription_status !== "trialing")
      return "/billing";
    return "/attorney/dashboard";
  }
  return "/";
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from?.pathname || routeByRole(user);
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      data-testid="login-page"
      className="py-16 lg:py-24 bg-white min-h-[80vh]"
    >
      <div className="max-w-md mx-auto px-6">
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-3 text-center">
          Welcome back
        </p>
        <h1 className="font-heading text-4xl font-bold text-[#0A192F] text-center">
          Sign in to LexIntake
        </h1>
        <p className="mt-3 text-center text-slate-600">
          Attorneys, law firms, and admins.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-5"
        >
          <div>
            <Label htmlFor="email" className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
              Email
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10 rounded-lg border-slate-300"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
                Password
              </Label>
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs text-[#C5A059] hover:underline"
              >
                Forgot?
              </Link>
            </div>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 rounded-lg border-slate-300"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg py-5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <span className="flex-1 h-px bg-slate-200" />
          <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Or</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        <Button
          type="button"
          data-testid="login-google-btn"
          onClick={handleGoogle}
          variant="outline"
          className="w-full rounded-lg py-5 border-slate-300 text-[#0A192F]"
        >
          Continue with Google
        </Button>

        <p className="mt-6 text-sm text-center text-slate-600">
          New to LexIntake?{" "}
          <Link
            to="/register"
            data-testid="login-to-register"
            className="text-[#0A192F] underline font-medium"
          >
            Create an attorney account
          </Link>
        </p>
        <p className="mt-2 text-sm text-center text-slate-600">
          Have a legal issue?{" "}
          <Link to="/intake" className="text-[#0A192F] underline font-medium">
            Start a free case review
          </Link>
        </p>
      </div>
    </div>
  );
}
